import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas, faXmark, faUser, faCirclePlus, faLightbulb, faLocationDot, faEllipsis, faChevronLeft } from '@fortawesome/free-solid-svg-icons'

import mixpanel from "mixpanel-browser";

import CoreApp from './CoreApp';
import Landing from './Landing';

import { auth, signInWithGoogle, getUserIdByUsername, getProfileInfo } from './functions/firebase';
import { getUIDFromURL } from './functions/util_fn';
import Profile from './pages/profile';
import NewPost from './pages/newPost';
import AllPostsFeed from './components/allPostsFeed';
import HobbiesList from './components/hobbiesList';
import HobbyPage from './pages/hobbyPage';
import EditProfile from './pages/editProfile';
import SinglePostPage from './pages/singlePostPage';
import SingleProjectPage from './pages/singleProjectPage';
import ProjectGallery from './pages/ProjectGallery';
import ProjectsList from './components/projectsList';
import AllUsers from './pages/AllUsers';
import AboutPage from './pages/aboutPage';
import AdminHome from './pages/admin-pages/adminHome';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient();

function App() {

  // Near entry of your product, init Mixpanel
  mixpanel.init("073900a606ddd9cc7dc171a756852b27", {
    debug: true,
    track_pageview: true,
    persistence: "localStorage",
  });

  const [user, setUser] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  library.add(fas, faXmark, faUser, faCirclePlus, faLightbulb, faLocationDot, faEllipsis, faChevronLeft);

  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        console.log('*** User logged in', user);
        mixpanel.identify(user.uid);
        setAuthenticatedUser(user);

        // Check if the URL contains a username or uid
        const pathParts = location.pathname.split('/');
        const userIdentifier = pathParts[2]; // Get identifier after /u/

        if (userIdentifier) {
          // Check if the identifier is a UID (assuming UIDs are longer than usernames)
          if (userIdentifier.length > 20) {
            if (userIdentifier === user.uid) {
              // Get profile info for authenticated user
              const profile = await getProfileInfo(user.uid);
              setUser({ ...user, username: profile?.username });
            } else {
              // Get profile info for other user
              const profile = await getProfileInfo(userIdentifier);
              setUser({ uid: userIdentifier, username: profile?.username });
            }
          } else {
            // It's a username, look up the corresponding UID
            const uid = await getUserIdByUsername(userIdentifier);
            if (uid) {
              if (uid === user.uid) {
                const profile = await getProfileInfo(uid);
                setUser({ ...user, username: profile?.username });
              } else {
                const profile = await getProfileInfo(uid);
                setUser({ uid, username: profile?.username });
              }
            } else {
              console.log('Username not found');
              setUser(null);
            }
          }
        } else {
          setUser(null);
        }
      } else {
        console.log('*** No user logged in');
        
        // Similar logic for non-authenticated users
        const pathParts = location.pathname.split('/');
        const userIdentifier = pathParts[2];
        
        if (userIdentifier) {
          if (userIdentifier.length > 20) {
            const profile = await getProfileInfo(userIdentifier);
            setUser({ uid: userIdentifier, username: profile?.username });
          } else {
            const uid = await getUserIdByUsername(userIdentifier);
            if (uid) {
              const profile = await getProfileInfo(uid);
              setUser({ uid, username: profile?.username });
            } else {
              setUser(null);
            }
          }
        } else {
          setUser(null);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [location]);


  const signInThenRedirect = async () => {
    var didSignIn = await signInWithGoogle();
    console.log(didSignIn);
    if (didSignIn) {
      // Get the user's username from their profile
      const profile = await getProfileInfo(user.uid);
      if (profile && profile.username) {
        navigate(`/u/${profile.username}/profile/about`, {});
      } else {
        navigate(`/u/${user.uid}/profile/about`, {});
      }
    }
  }

  if (loading) {
      return <div></div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/u">
          <Route path=":userID" element={<Navigate to="profile/about" replace />} />
          <Route path=":userID" element={<CoreApp user={user} />}>
            <Route path="profile" element={<Profile user={user} />}>
              <Route path="feed" element={<AllPostsFeed user={user}/>}></Route>
              <Route path="hobbies" element={<HobbiesList user={user}/>}></Route>
              <Route path="about" element={<AboutPage user={user}/>}></Route>
              <Route path="projects" element={<ProjectsList user={user}/>} />
            </Route>
            <Route path="hobby/:hid" element={<HobbyPage user={user} />} />
            <Route path="project/:pid" element={<SingleProjectPage user={user} />} />
            <Route path="project/:pid/gallery" element={<ProjectGallery user={user} />} />
            <Route path="post/:postId" element={<SinglePostPage user={user} />} />
            <Route path="new" element={<NewPost user={user}/>}></Route>
            <Route path="edit" element={<EditProfile user={user}/>}></Route>
            <Route path="edit-post/:postId" element={<NewPost user={user} mode="edit" />}></Route>
          </Route>
        </Route>
        <Route path="/" element={<Landing user={user} GoogleLogIn={signInThenRedirect} />}></Route>
        <Route path="/allusers" element={<AllUsers />}></Route>
        <Route path="/admin" element={<AdminHome />} />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
